import { useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { type ContactPatch, useAddContact, useDeleteContact, usePatchContact } from '@/features/leads/api/contacts'
import { useLogEvent, useSaveOutcome, useSkipLead, useUpdateLead } from '@/shared/api/leads'
import { swapPrimaryByKey } from './leadUtils'
import {
  LEADS_KEY, appendTimelineItem, editLeadInCache, markSkipped, mergeFields,
} from './leadCache'
import { notifyDoNotContactRefusal, notifyWriteFailed } from './writeFeedback'
import { isDoNotContactRefusal } from './doNotContactRules'
import { useCanEdit } from '@/shared/write-access'
import { trackEvent } from '@/shared/api/telemetry'
import { contactEditMeta } from '@/shared/api/trackMeta'
import type { Lead, TimelineItem, TimelineKind } from '@/shared/types'

const EVENT_KIND_LABEL: Partial<Record<TimelineKind, string>> = {
  call: 'samtalet',
  note: 'anteckningen',
  email: 'mejlet',
  meeting: 'mötet',
  primary_changed: 'kontaktändringen',
}

export interface EventInput {
  kind: string
  outcome?: string
  body?: string
  payload?: Record<string, unknown>
}

const PATCH_KEYS = new Set([
  'stage', 'seller', 'meetingAt', 'meetingOutcome', 'gotContact', 'bookedMeeting',
  'comment', 'nextContactAt', 'nextContactNote', 'isCallbackAlert',
  'disqualifiedReason', 'feedback', 'primaryContactOverride',
  'moveDate', 'officeHotel', 'likelyBooking',
])

export interface LeadWrites {
  /** Returns an undo for the optimistic patch — callers that want save
   *  feedback with an Undo action (the outcome flow) can hang onto it;
   *  everyone else is free to ignore the return value. */
  writeError: string | null
  updateLead: (id: string, patch: Partial<Lead>, event?: EventInput) => () => void
  logEvent: (id: string, event: EventInput) => () => void
  /** The outcome flow's write: the field patch and its timeline event commit
   *  in one request, so a dropped request can never leave one applied
   *  without the other (YLLW-CRM-107). Same optimistic-then-mutate shape as
   *  `updateLead`/`logEvent`; nothing offers an Undo after an outcome, so it
   *  returns none. */
  saveOutcome: (id: string, patch: Partial<Lead>, event: EventInput) => void
  setPrimaryContact: (leadId: string, contactKey: string | null, contactName: string) => void
  patchContact: (leadId: string, contactKey: string, edits: ContactPatch) => Promise<void>
  addContact: (leadId: string, fields: ContactPatch & { name: string }) => Promise<void>
  deleteContact: (leadId: string, contactKey: string) => Promise<void>
}

/**
 * Every write to a lead, callable from wherever the write happens.
 *
 * Takes no arguments on purpose. It used to need setLeads and a patch ref from
 * useLeadsData, which meant only the shell could call it, which meant six
 * callbacks were prop-drilled from App through Focus, Table and the overlay
 * down to the cards that actually used them. Optimistic writes go to the query
 * cache now, so there is nothing to hand around.
 */
export function useLeadWrites(): LeadWrites {
  // Read-only guard. Every lead write in the app funnels through this hook, so
  // one check here covers the lot — including controls that commit on a single
  // click (stage select, JA/NEJ toggles, star rating) which are the easy ones to
  // hit by accident while looking something up. Disabling the inputs is the
  // visible half; this is the half that holds if one is missed.
  const canEdit = useCanEdit()
  const qc = useQueryClient()
  // Mutations write their optimistic value into the query cache, then PATCH or
  // POST in the background. Each mutation's own onSuccess splices the server's
  // canonical row in; the onError handlers here restore the previous rows, so
  // a failed write leaves no trace instead of inverting its own patch.
  const updateLeadMutation = useUpdateLead()
  const pendingBookings = useRef(new Set<string>())
  const bookingRetryIds = useRef(new Map<string, string>())
  const logEventMutation = useLogEvent()
  const saveOutcomeMutation = useSaveOutcome()
  const patchContactMutation = usePatchContact()
  const addContactMutation = useAddContact()
  const deleteContactMutation = useDeleteContact()

  // updateLead and logEvent are the only two mutations handed to every row of
  // the table, so they are memoized: an unstable identity here would defeat
  // TableRow's React.memo and re-render the whole table on any parent render.
  // .mutate is a stable reference; the mutation result object is not.
  const updateLeadMutate = updateLeadMutation.mutate
  const saveOutcomeAsync = saveOutcomeMutation.mutateAsync
  const logEventMutate = logEventMutation.mutate
  const saveOutcomeMutate = saveOutcomeMutation.mutate

  const updateLead = useCallback((id: string, patch: Partial<Lead>, event?: EventInput): (() => void) => {
    if (!canEdit || pendingBookings.current.has(id)) return () => {}
    // Forward only the whitelisted PATCH fields to the server
    const apiPatch: Record<string, unknown> = {}
    for (const k of Object.keys(patch)) {
      if (PATCH_KEYS.has(k)) apiPatch[k] = (patch as Record<string, unknown>)[k]
    }

    // Wrapped so a failed write's Retry action can re-run the exact same
    // optimistic-apply-then-mutate sequence rather than just the mutate half.
    const attempt = (): (() => void) => {
      // Stop any refetch already in flight before writing, so a server snapshot
      // taken before this edit cannot land on top of it.
      void qc.cancelQueries({ queryKey: LEADS_KEY })
      const undo = editLeadInCache(qc, id, mergeFields(patch))

      if (Object.keys(apiPatch).length === 0) return undo

      if (event) {
        pendingBookings.current.add(id)
        const eventId = bookingRetryIds.current.get(id) ?? crypto.randomUUID()
        bookingRetryIds.current.set(id, eventId)
        void saveOutcomeAsync({ id, eventId, patch: apiPatch as Partial<Lead>, event })
          .then(() => bookingRetryIds.current.delete(id))
          .catch(err => {
            trackEvent('write_failed', { op: 'save_booking' }, id)
            undo()
            notifyWriteFailed(qc, id, 'bokningen', () => { attempt() }, err)
          })
          .finally(() => pendingBookings.current.delete(id))
        return undo
      }
      if (patch.bookedMeeting === false) bookingRetryIds.current.delete(id)
      updateLeadMutate(
        { id, patch: apiPatch as Partial<Lead> },
        // The mutation's own onSuccess splices the server's canonical row in.
        // Only the failure path is handled here, and it restores the rows
        // exactly as they were rather than trying to invert the patch.
        //
        // The rollback used to be silent, which is precisely why the failure
        // has to be reported: the edit vanishes from the screen and nothing
        // anywhere says it was ever attempted. No success toast — the server
        // records the stage change and the timeline side-effects itself, and
        // the outcome flow has its own confirmation for the writes it drives.
        {
          onError: err => {
            trackEvent('write_failed', { op: 'update_lead', fields: Object.keys(apiPatch).sort() }, id)
            undo()
            notifyWriteFailed(qc, id, 'uppdateringen', () => { attempt() }, err)
          },
        },
      )
      return undo
    }
    return attempt()
  }, [canEdit, qc, updateLeadMutate, saveOutcomeAsync])

  // Append a timeline event (call attempt, note, rating event…). Server applies
  // side-effects (e.g. call with outcome=Reached → got_contact + stage=Contacted).
  const logEvent = useCallback((id: string, event: EventInput): (() => void) => {
    if (!canEdit) return () => {}
    const stamped: TimelineItem = {
      kind: event.kind as TimelineKind,
      at: new Date().toISOString(),
      by: 'me',
      body: event.body,
      outcome: event.outcome,
    }
    const action = EVENT_KIND_LABEL[stamped.kind] ?? 'anteckningen'
    const attempt = (): (() => void) => {
      void qc.cancelQueries({ queryKey: LEADS_KEY })
      const undo = editLeadInCache(qc, id, appendTimelineItem(stamped))
      // Success is the server's to record: it already writes note_added,
      // call_logged, meeting_booked and lead_stage_changed off the same request.
      logEventMutate({ id, event }, {
        onError: err => {
          trackEvent('write_failed', { op: 'log_event', kind: event.kind }, id)
          undo()
          notifyWriteFailed(qc, id, action, () => { attempt() }, err)
        },
      })
      return undo
    }
    return attempt()
  }, [canEdit, qc, logEventMutate])

  // The outcome sheet's write: PATCHing the lead's fields and POSTing the
  // timeline event used to be two independent requests, so a WAF or network
  // drop of just one of them left an outcome logged with no stage/follow-up
  // effect, or the reverse (YLLW-CRM-107). One combined request with a
  // stable id fixes both the split and the double-submit: a retry (by this
  // function or the failure toast) resends the same id, and the server
  // treats a repeat of it as the write already done.
  const saveOutcome = useCallback((id: string, patch: Partial<Lead>, event: EventInput): void => {
    if (!canEdit) return
    const apiPatch: Record<string, unknown> = {}
    for (const k of Object.keys(patch)) {
      if (PATCH_KEYS.has(k)) apiPatch[k] = (patch as Record<string, unknown>)[k]
    }
    const stamped: TimelineItem = {
      kind: event.kind as TimelineKind,
      at: new Date().toISOString(),
      by: 'me',
      body: event.body,
      outcome: event.outcome,
    }
    const eventId = crypto.randomUUID()

    const attempt = (): void => {
      void qc.cancelQueries({ queryKey: LEADS_KEY })
      const undoPatch = editLeadInCache(qc, id, mergeFields(patch))
      const undoEvent = editLeadInCache(qc, id, appendTimelineItem(stamped))
      const undo = () => { undoEvent(); undoPatch() }

      saveOutcomeMutate(
        { id, eventId, patch: apiPatch as Partial<Lead>, event },
        {
          onError: err => {
            trackEvent('write_failed', { op: 'save_outcome' }, id)
            undo()
            notifyWriteFailed(qc, id, 'utfallet', attempt, err)
          },
        },
      )
    }
    attempt()
  }, [canEdit, qc, saveOutcomeMutate])

  // Promote a different contact as primary on a lead, or clear the override
  // by passing null. Always logs a 'primary_changed' timeline event so the
  // history is auditable and we can measure how often the AI gets it right.
  // Optimistically swaps handover.primary client-side so the card flips
  // instantly; the response splice then reconciles with the server's canonical
  // merged handover. Clearing the override (null) waits for the splice because
  // the AI's pick isn't knowable client-side.
  //
  // The override and its timeline entry go as one request with one id, as the
  // outcome sheet's do. As a PATCH plus a separate POST, a Retry after a
  // dropped response, or a second click, logged the change twice (YCRM-59):
  // now a retry resends the same id, and the server writes no entry for a
  // change to the override the lead already holds.
  const setPrimaryContact = (
    leadId: string,
    contactKey: string | null,
    contactName: string,
  ) => {
    // The optimistic swap below would otherwise show a change that the
    // read-only guard never lets reach the server.
    if (!canEdit) return
    // `cleared` separates "the AI picked wrong, here is the right person" from
    // "the AI was right after all" — the two say opposite things about the pick.
    trackEvent('primary_contact_set', { cleared: contactKey === null }, leadId)
    const body = contactKey
      ? `Gjorde ${contactName} till primär kontakt`
      : 'Återställde till AI-vald kontakt'
    const stamped: TimelineItem = { kind: 'primary_changed', at: new Date().toISOString(), by: 'me', body }
    const eventId = crypto.randomUUID()

    const attempt = () => {
      void qc.cancelQueries({ queryKey: LEADS_KEY })
      const undo = editLeadInCache(qc, leadId, l => {
        const logged = appendTimelineItem(stamped)(l)
        return contactKey ? swapPrimaryByKey(logged, contactKey) : logged
      })
      logEventMutate(
        { id: leadId, event: { kind: 'primary_changed', body, id: eventId, leadPatch: { primaryContactOverride: contactKey } } },
        {
          onError: () => {
            trackEvent('write_failed', { op: 'set_primary' }, leadId)
            undo()
            notifyWriteFailed(qc, leadId, 'kontaktändringen', attempt)
          },
        },
      )
    }
    attempt()
  }

  // Contact edits don't get optimistic local updates — the merged contact
  // shape (id assignment, edited flag, deletion ordering) is derived server-
  // side from contact_overrides + the canonical contacts table, so the easy
  // path is to fire the mutation and let the cache invalidation refresh the
  // card. Errors throw out of the awaited mutateAsync so the inline form
  // keeps the BDR's edits on screen.
  // The three contact writes are the ones the server logs nothing for, so the
  // client is the only place they can be counted. Reported after the await so a
  // rejected write is a `write_failed`, not a phantom edit — and the metadata
  // carries field names and counts only, never what was typed.
  const patchContact = async (leadId: string, contactKey: string, edits: ContactPatch) => {
    if (!canEdit) return
    try {
      await patchContactMutation.mutateAsync({ leadId, contactKey, edits })
    } catch (err) {
      trackEvent('write_failed', { op: 'contact_edit' }, leadId)
      throw err
    }
    trackEvent('contact_edited', contactEditMeta(edits), leadId)
  }
  const addContact = async (
    leadId: string,
    fields: ContactPatch & { name: string },
  ) => {
    if (!canEdit) return
    try {
      await addContactMutation.mutateAsync({ leadId, fields })
    } catch (err) {
      trackEvent('write_failed', { op: 'contact_add' }, leadId)
      throw err
    }
    trackEvent('contact_added', contactEditMeta(fields), leadId)
  }
  const deleteContact = async (leadId: string, contactKey: string) => {
    if (!canEdit) return
    try {
      await deleteContactMutation.mutateAsync({ leadId, contactKey })
    } catch (err) {
      trackEvent('write_failed', { op: 'contact_remove' }, leadId)
      throw err
    }
    trackEvent('contact_removed', undefined, leadId)
  }

  return {
    writeError: saveOutcomeMutation.error
      ? 'Kunde inte spara bokningen. Försök igen.'
      : updateLeadMutation.error || logEventMutation.error
        ? 'Kunde inte spara ändringen. Försök igen.'
        : null,
    updateLead,
    logEvent,
    saveOutcome,
    setPrimaryContact,
    patchContact,
    addContact,
    deleteContact,
  }
}

/**
 * Postponing is the one write bound to the queue: it hides the lead and moves
 * the seller on, so it needs to know how to advance.
 */
export function usePostponeLead(advance: () => void): (leadId: string, days: number) => void {
  const canEdit = useCanEdit()
  const qc = useQueryClient()
  const skipLeadMutation = useSkipLead()

  // Postpone a lead — fires the skip endpoint with a days parameter so the
  // lead disappears from this BDR's focus queue for N days without writing
  // any timeline event (so dashboard call/meeting counters stay untouched).
  // Optimistically marks the lead skipped so the local focus filter hides it
  // immediately; the splice from the PATCH response reconciles the
  // skipped_until value with the server's canonical state.
  return (leadId: string, days: number) => {
    if (!canEdit) return
    if (!Number.isFinite(days) || days <= 0) return
    void qc.cancelQueries({ queryKey: LEADS_KEY })
    const undo = editLeadInCache(qc, leadId, markSkipped)
    // Deliberately writes no timeline event, so the skip endpoint leaves no
    // trace of its own — this is the only record that the lead was pushed away
    // rather than worked, and `days` is the interesting half of it.
    trackEvent('lead_postponed', { days }, leadId)
    skipLeadMutation.mutate({ id: leadId, days }, {
      onError: err => {
        trackEvent('write_failed', { op: 'postpone' }, leadId)
        undo()
        if (isDoNotContactRefusal(err)) notifyDoNotContactRefusal(qc, leadId)
      },
    })
    advance()
  }

}
